import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '../components/ui/dialog';
import { 
    Plus, 
    Trash2, 
    CreditCard, 
    Banknote, 
    QrCode, 
    User,
    ShoppingCart,
    Search,
    Minus,
    Clock,
    Calendar,
    Loader2,
    Receipt,
    AlertTriangle,
    ScanBarcode,
    Eye
} from 'lucide-react';
import { toast } from 'sonner';
import useBarcodeScanner from '../hooks/useBarcodeScanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ComandasPage = () => {
    const [tabs, setTabs] = useState([]);
    const [fiados, setFiados] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Dialogs
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [tabDetailOpen, setTabDetailOpen] = useState(false);
    const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
    const [closeTabDialogOpen, setCloseTabDialogOpen] = useState(false);
    const [fiadosDialogOpen, setFiadosDialogOpen] = useState(false);
    const [payFiadoDialogOpen, setPayFiadoDialogOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    
    // Selected tab
    const [selectedTab, setSelectedTab] = useState(null);
    const [selectedFiado, setSelectedFiado] = useState(null);
    
    // Form states
    const [customerName, setCustomerName] = useState('');
    const [productSearch, setProductSearch] = useState('');
    const [scannerEnabled, setScannerEnabled] = useState(false);
    
    // Payment states
    const [paymentMethod, setPaymentMethod] = useState('dinheiro');
    const [discount, setDiscount] = useState(0);
    const [amountPaid, setAmountPaid] = useState('');
    const [changeGiven, setChangeGiven] = useState(0);
    const [fiadoDueDate, setFiadoDueDate] = useState('');
    
    // Split payment
    const [splitPaymentMode, setSplitPaymentMode] = useState(false);
    const [payments, setPayments] = useState([]);
    const [currentPaymentMethod, setCurrentPaymentMethod] = useState('dinheiro');
    const [currentPaymentAmount, setCurrentPaymentAmount] = useState('');
    
    const [processing, setProcessing] = useState(false);

    // Fetch data
    const fetchTabs = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/tabs`);
            const allTabs = response.data;
            setTabs(allTabs.filter(t => t.status === 'aberta' || t.status === 'fechando'));
            setFiados(allTabs.filter(t => t.status === 'fiado'));
        } catch (error) {
            console.error('Error fetching tabs:', error);
            toast.error('Erro ao carregar comandas');
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/products`);
            setProducts(response.data.filter(p => p.active !== false && p.stock_quantity > 0));
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchTabs(), fetchProducts()]);
            setLoading(false);
        };
        loadData();
    }, []);

    // Scanner handler for adding products
    const handleBarcodeScan = useCallback(async (barcode) => {
        if (!selectedTab || !addProductDialogOpen) return;
        
        setScannerEnabled(false);
        
        try {
            // Clean barcode if needed
            let cleanBarcode = barcode;
            if (barcode.length >= 8 && /^[A-Za-z]/.test(barcode)) {
                const numbersOnly = barcode.replace(/[^0-9]/g, '');
                if (numbersOnly.length >= 8) {
                    cleanBarcode = numbersOnly;
                }
            }
            
            const response = await axios.get(`${API_URL}/api/products/barcode/${cleanBarcode}`);
            const product = response.data;
            
            if (product.active === false) {
                toast.error('Produto inativo');
                return;
            }
            
            await addProductToTab(product);
            
        } catch (error) {
            if (error.response?.status === 404) {
                toast.error(`Produto não encontrado: ${barcode}`);
            } else {
                toast.error('Erro ao buscar produto');
            }
        } finally {
            setTimeout(() => setScannerEnabled(true), 500);
        }
    }, [selectedTab, addProductDialogOpen]);

    const { isScanning } = useBarcodeScanner({
        enabled: scannerEnabled && addProductDialogOpen,
        onScan: handleBarcodeScan,
        minLength: 4,
        maxDelay: 150
    });

    // Create tab
    const handleCreateTab = async () => {
        if (!customerName.trim()) {
            toast.error('Informe o nome do cliente');
            return;
        }
        
        try {
            const response = await axios.post(`${API_URL}/api/tabs`, {
                customer_name: customerName.trim()
            });
            
            toast.success('Comanda criada!');
            setCreateDialogOpen(false);
            setCustomerName('');
            fetchTabs();
            
            // Open the new tab
            setSelectedTab(response.data);
            setAddProductDialogOpen(true);
            setScannerEnabled(true);
        } catch (error) {
            toast.error('Erro ao criar comanda');
        }
    };

    // Add product to tab
    const addProductToTab = async (product) => {
        if (!selectedTab) return;
        
        try {
            const price = product.on_promotion && product.promotional_price 
                ? product.promotional_price 
                : product.price;
            
            const response = await axios.post(`${API_URL}/api/tabs/${selectedTab.id}/items`, {
                product_id: product.id,
                product_name: product.name,
                quantity: 1,
                unit_price: price
            });
            
            const updatedTab = response.data;
            setSelectedTab(updatedTab);
            
            // ⭐ Atualizar também a lista de comandas em tempo real
            setTabs(prevTabs => prevTabs.map(t => 
                t.id === updatedTab.id ? updatedTab : t
            ));
            
            toast.success(`${product.name} adicionado!`);
        } catch (error) {
            const errorMsg = error.response?.data?.detail || 'Erro ao adicionar produto';
            toast.error(errorMsg);
        }
    };

    // Remove product from tab
    const removeProductFromTab = async (itemId) => {
        if (!selectedTab) return;
        
        try {
            const response = await axios.delete(`${API_URL}/api/tabs/${selectedTab.id}/items/${itemId}`);
            const updatedTab = response.data;
            setSelectedTab(updatedTab);
            
            // ⭐ Atualizar lista em tempo real
            setTabs(prevTabs => prevTabs.map(t => 
                t.id === updatedTab.id ? updatedTab : t
            ));
            
            toast.success('Item removido');
        } catch (error) {
            const errorMsg = error.response?.data?.detail || 'Erro ao remover item';
            toast.error(errorMsg);
        }
    };

    // Update item quantity
    const updateItemQuantity = async (itemId, newQuantity) => {
        if (!selectedTab || newQuantity < 1) return;
        
        try {
            const response = await axios.put(
                `${API_URL}/api/tabs/${selectedTab.id}/item/${itemId}/quantity?quantity=${newQuantity}`
            );
            const updatedTab = response.data;
            setSelectedTab(updatedTab);
            
            // ⭐ Atualizar lista em tempo real
            setTabs(prevTabs => prevTabs.map(t => 
                t.id === updatedTab.id ? updatedTab : t
            ));
        } catch (error) {
            const errorMsg = error.response?.data?.detail || 'Erro ao atualizar quantidade';
            toast.error(errorMsg);
        }
    };

    // Close tab
    const handleCloseTab = async () => {
        if (!selectedTab) return;
        
        const total = Math.max(0, selectedTab.subtotal - discount);
        
        // Validate split payment
        if (splitPaymentMode) {
            const totalPayments = payments.reduce((sum, p) => sum + p.amount, 0);
            if (totalPayments < total) {
                toast.error(`Falta pagar: R$ ${(total - totalPayments).toFixed(2)}`);
                return;
            }
        }
        
        // Validate fiado
        if (paymentMethod === 'fiado' && !fiadoDueDate) {
            toast.error('Informe a data de pagamento do fiado');
            return;
        }
        
        // Validate cash payment
        if (!splitPaymentMode && paymentMethod === 'dinheiro') {
            const paid = parseFloat(amountPaid) || 0;
            if (paid < total) {
                toast.error('Valor recebido insuficiente');
                return;
            }
        }
        
        setProcessing(true);
        
        try {
            await axios.post(`${API_URL}/api/tabs/${selectedTab.id}/close`, {
                payment_method: splitPaymentMode ? 'dividido' : paymentMethod,
                payments: splitPaymentMode ? payments : null,
                amount_paid: !splitPaymentMode && paymentMethod === 'dinheiro' ? parseFloat(amountPaid) : null,
                change_given: !splitPaymentMode && paymentMethod === 'dinheiro' ? changeGiven : null,
                fiado_due_date: paymentMethod === 'fiado' ? fiadoDueDate : null,
                discount: discount
            });
            
            if (paymentMethod === 'fiado') {
                toast.success('Comanda registrada como fiado!');
            } else {
                toast.success('Comanda fechada com sucesso!');
            }
            
            setCloseTabDialogOpen(false);
            setTabDetailOpen(false);
            setSelectedTab(null);
            resetPaymentForm();
            fetchTabs();
        } catch (error) {
            const msg = error.response?.data?.detail || 'Erro ao fechar comanda';
            toast.error(msg);
        } finally {
            setProcessing(false);
        }
    };

    // Pay fiado
    const handlePayFiado = async () => {
        if (!selectedFiado) return;
        
        setProcessing(true);
        
        try {
            await axios.post(`${API_URL}/api/tabs/${selectedFiado.id}/pay-fiado`, {
                payment_method: paymentMethod,
                amount_paid: paymentMethod === 'dinheiro' ? parseFloat(amountPaid) : null,
                change_given: paymentMethod === 'dinheiro' ? changeGiven : null
            });
            
            toast.success('Fiado pago com sucesso!');
            setPayFiadoDialogOpen(false);
            setFiadosDialogOpen(false);
            setSelectedFiado(null);
            resetPaymentForm();
            fetchTabs();
        } catch (error) {
            const msg = error.response?.data?.detail || 'Erro ao pagar fiado';
            toast.error(msg);
        } finally {
            setProcessing(false);
        }
    };

    // Cancel fiado
    const handleCancelFiado = async (fiado) => {
        if (!window.confirm(`Deseja cancelar o fiado de ${fiado.customer_name}?\n\nO estoque dos produtos será devolvido.`)) {
            return;
        }
        
        try {
            const response = await axios.put(`${API_URL}/api/tabs/${fiado.id}/cancel-fiado`);
            toast.success(response.data.message || 'Fiado cancelado com sucesso!');
            fetchTabs();
        } catch (error) {
            const msg = error.response?.data?.detail || 'Erro ao cancelar fiado';
            toast.error(msg);
        }
    };

    // Delete tab
    const handleDeleteTab = async () => {
        if (!selectedTab) return;
        
        setProcessing(true); // Adicionar loading state
        
        try {
            await axios.delete(`${API_URL}/api/tabs/${selectedTab.id}`);
            toast.success('Comanda excluída');
            setDeleteConfirmOpen(false);
            setTabDetailOpen(false);
            setSelectedTab(null);
            await fetchTabs(); // Aguardar atualização
        } catch (error) {
            console.error('Erro ao excluir comanda:', error);
            const errorMsg = error.response?.data?.detail || 'Erro ao excluir comanda';
            toast.error(errorMsg);
        } finally {
            setProcessing(false); // Sempre liberar loading
        }
    };

    // Reset payment form
    const resetPaymentForm = () => {
        setPaymentMethod('dinheiro');
        setDiscount(0);
        setAmountPaid('');
        setChangeGiven(0);
        setFiadoDueDate('');
        setSplitPaymentMode(false);
        setPayments([]);
        setCurrentPaymentMethod('dinheiro');
        setCurrentPaymentAmount('');
    };

    // Calculate change
    useEffect(() => {
        if (selectedTab && paymentMethod === 'dinheiro' && amountPaid) {
            const total = Math.max(0, selectedTab.subtotal - discount);
            const paid = parseFloat(amountPaid) || 0;
            setChangeGiven(Math.max(0, paid - total));
        } else {
            setChangeGiven(0);
        }
    }, [amountPaid, selectedTab, discount, paymentMethod]);

    // Add payment (split)
    const addPayment = () => {
        const amount = parseFloat(currentPaymentAmount) || 0;
        if (amount <= 0) {
            toast.error('Informe um valor válido');
            return;
        }
        
        setPayments([...payments, { method: currentPaymentMethod, amount }]);
        setCurrentPaymentAmount('');
    };

    const removePayment = (index) => {
        setPayments(payments.filter((_, i) => i !== index));
    };

    const formatCurrency = (value) => {
        return `R$ ${(value || 0).toFixed(2)}`;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'aberta': return 'bg-green-500';
            case 'fechando': return 'bg-yellow-500';
            case 'fiado': return 'bg-orange-500';
            case 'paga': return 'bg-gray-500';
            default: return 'bg-gray-500';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'aberta': return 'Aberta';
            case 'fechando': return 'Fechando';
            case 'fiado': return 'Fiado';
            case 'paga': return 'Paga';
            default: return status;
        }
    };

    const filteredProducts = products.filter(p => 
        p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
        (p.barcode && p.barcode.includes(productSearch))
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in-up">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white">Comandas</h1>
                    <p className="text-gray-400 text-sm mt-1">
                        Gerenciar comandas de clientes no local
                    </p>
                </div>
                <div className="flex gap-2">
                    {fiados.length > 0 && (
                        <Button
                            variant="outline"
                            className="border-orange-500/30 text-orange-500 hover:bg-orange-500/10"
                            onClick={() => setFiadosDialogOpen(true)}
                        >
                            <Clock className="w-4 h-4 mr-2" />
                            Fiados ({fiados.length})
                        </Button>
                    )}
                    <Button
                        className="bg-red-500 text-white hover:bg-red-400 font-bold"
                        onClick={() => setCreateDialogOpen(true)}
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Nova Comanda
                    </Button>
                </div>
            </div>

            {/* Tabs Grid */}
            {tabs.length === 0 ? (
                <Card className="bg-[#0a0a0a] border-[#262626]">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                        <Receipt className="w-16 h-16 text-gray-600 mb-4" />
                        <h3 className="text-xl font-bold text-white mb-2">Nenhuma comanda aberta</h3>
                        <p className="text-gray-400 mb-4">Crie uma nova comanda para começar</p>
                        <Button
                            className="bg-red-500 text-white hover:bg-red-400"
                            onClick={() => setCreateDialogOpen(true)}
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Nova Comanda
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                    {tabs.map(tab => (
                        <Card
                            key={tab.id}
                            className={`cursor-pointer transition-all hover:scale-105 border-2 ${
                                tab.status === 'aberta' 
                                    ? 'bg-green-500/10 border-green-500/50 hover:border-green-500' 
                                    : 'bg-yellow-500/10 border-yellow-500/50 hover:border-yellow-500'
                            }`}
                            onClick={() => {
                                setSelectedTab(tab);
                                setTabDetailOpen(true);
                            }}
                        >
                            <CardContent className="p-4 text-center">
                                <div className={`w-12 h-12 mx-auto rounded-full ${getStatusColor(tab.status)} flex items-center justify-center mb-3`}>
                                    <User className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="font-bold text-white text-lg mb-2 px-1 break-words leading-tight min-h-[2.5rem] flex items-center justify-center">
                                    {tab.customer_name}
                                </h3>
                                <p className="text-xl font-bold text-white mb-2">
                                    {formatCurrency(tab.total)}
                                </p>
                                <Badge className={`${
                                    tab.status === 'aberta' 
                                        ? 'bg-green-500/20 text-green-400' 
                                        : 'bg-yellow-500/20 text-yellow-400'
                                }`}>
                                    {tab.items?.length || 0} itens
                                </Badge>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Create Tab Dialog */}
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white">
                    <DialogHeader>
                        <DialogTitle>Nova Comanda</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nome do Cliente</Label>
                            <Input
                                value={customerName}
                                onChange={(e) => setCustomerName(e.target.value)}
                                placeholder="Digite o nome..."
                                className="bg-[#121212] border-[#262626] text-white"
                                autoFocus
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="border-[#262626] text-gray-400">
                            Cancelar
                        </Button>
                        <Button onClick={handleCreateTab} className="bg-red-500 text-white hover:bg-red-400">
                            Criar Comanda
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Tab Detail Dialog */}
            <Dialog open={tabDetailOpen} onOpenChange={(open) => {
                setTabDetailOpen(open);
                if (!open) setSelectedTab(null);
            }}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full ${getStatusColor(selectedTab?.status)} flex items-center justify-center`}>
                                <User className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className="font-bold">{selectedTab?.customer_name}</p>
                                <p className="text-sm text-gray-400">{getStatusLabel(selectedTab?.status)}</p>
                            </div>
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-y-auto py-4 space-y-4">
                        {/* Items List */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label className="text-gray-400">Itens da Comanda</Label>
                                <Button
                                    size="sm"
                                    onClick={() => {
                                        setAddProductDialogOpen(true);
                                        setScannerEnabled(true);
                                    }}
                                    className="bg-red-500 text-white hover:bg-red-400"
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Adicionar
                                </Button>
                            </div>
                            
                            {selectedTab?.items?.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                    <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>Nenhum item adicionado</p>
                                </div>
                            ) : (
                                <div className="space-y-2 max-h-60 overflow-y-auto">
                                    {selectedTab?.items?.map(item => (
                                        <div key={item.id} className="flex items-center justify-between p-3 bg-[#121212] rounded-lg">
                                            <div className="flex-1">
                                                <p className="text-white font-medium">{item.product_name}</p>
                                                <p className="text-sm text-gray-400">
                                                    {formatCurrency(item.unit_price)} x {item.quantity}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center gap-1 bg-[#1a1a1a] rounded-lg">
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-8 w-8 p-0 text-gray-400 hover:text-white"
                                                        onClick={() => updateItemQuantity(item.id, item.quantity - 1)}
                                                        disabled={item.quantity <= 1}
                                                    >
                                                        <Minus className="w-4 h-4" />
                                                    </Button>
                                                    <span className="w-8 text-center text-white">{item.quantity}</span>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-8 w-8 p-0 text-gray-400 hover:text-white"
                                                        onClick={() => updateItemQuantity(item.id, item.quantity + 1)}
                                                    >
                                                        <Plus className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                                <span className="w-20 text-right font-bold text-white">
                                                    {formatCurrency(item.total_price)}
                                                </span>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-8 w-8 p-0 text-red-500 hover:bg-red-500/20"
                                                    onClick={() => removeProductFromTab(item.id)}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        {/* Total */}
                        <div className="p-4 bg-[#121212] rounded-lg">
                            <div className="flex justify-between text-xl font-bold">
                                <span className="text-white">Total</span>
                                <span className="text-red-500">{formatCurrency(selectedTab?.total)}</span>
                            </div>
                        </div>
                    </div>
                    
                    <DialogFooter className="gap-2">
                        {selectedTab?.status !== 'fiado' && (
                            <Button
                                variant="outline"
                                className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                                onClick={() => setDeleteConfirmOpen(true)}
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Excluir
                            </Button>
                        )}
                        
                        {selectedTab?.status === 'fiado' ? (
                            <>
                                <Button
                                    variant="outline"
                                    onClick={() => handleCancelFiado(selectedTab)}
                                    className="border-red-500 text-red-500 hover:bg-red-500/20"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Cancelar Fiado
                                </Button>
                                <Button
                                    onClick={() => {
                                        setSelectedFiado(selectedTab);
                                        resetPaymentForm();
                                        setPayFiadoDialogOpen(true);
                                        setTabDetailOpen(false);
                                    }}
                                    className="bg-green-500 text-white hover:bg-green-400 font-bold"
                                >
                                    <CreditCard className="w-4 h-4 mr-2" />
                                    Receber Pagamento
                                </Button>
                            </>
                        ) : (
                            <Button
                                onClick={() => {
                                    resetPaymentForm();
                                    setCloseTabDialogOpen(true);
                                }}
                                disabled={!selectedTab?.items?.length}
                                className="bg-red-500 text-white hover:bg-red-400 font-bold"
                            >
                                Fechar Conta
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Product Dialog */}
            <Dialog open={addProductDialogOpen} onOpenChange={(open) => {
                setAddProductDialogOpen(open);
                setScannerEnabled(open);
                if (!open) setProductSearch('');
            }}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white max-w-lg max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ShoppingCart className="w-5 h-5" />
                            Adicionar Produto
                            {isScanning && (
                                <Badge className="bg-red-500/20 text-red-400 animate-pulse">
                                    <ScanBarcode className="w-3 h-3 mr-1" />
                                    Lendo...
                                </Badge>
                            )}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                            <Input
                                value={productSearch}
                                onChange={(e) => setProductSearch(e.target.value)}
                                placeholder="Buscar produto ou código..."
                                className="pl-10 bg-[#121212] border-[#262626] text-white"
                            />
                        </div>
                        
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                            <ScanBarcode className="w-4 h-4" />
                            Scanner ativo - bipe o código de barras
                        </p>
                        
                        <ScrollArea className="h-[300px]">
                            <div className="space-y-2">
                                {filteredProducts.map(product => (
                                    <div
                                        key={product.id}
                                        className="flex items-center justify-between p-3 bg-[#121212] rounded-lg hover:bg-[#1a1a1a] cursor-pointer transition-colors"
                                        onClick={() => addProductToTab(product)}
                                    >
                                        <div>
                                            <p className="text-white font-medium">{product.name}</p>
                                            <p className="text-xs text-gray-500">{product.barcode || 'Sem código'}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-white font-bold">
                                                {formatCurrency(product.on_promotion && product.promotional_price ? product.promotional_price : product.price)}
                                            </p>
                                            <p className="text-xs text-gray-500">Estoque: {product.stock_quantity}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </ScrollArea>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddProductDialogOpen(false)} className="border-[#262626] text-gray-400">
                            Fechar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Close Tab Dialog */}
            <Dialog open={closeTabDialogOpen} onOpenChange={setCloseTabDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Fechar Conta - {selectedTab?.customer_name}</DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        {/* Total */}
                        <div className="p-4 bg-[#121212] rounded-lg">
                            <div className="flex justify-between">
                                <span className="text-gray-400">Subtotal</span>
                                <span className="text-white">{formatCurrency(selectedTab?.subtotal)}</span>
                            </div>
                            {discount > 0 && (
                                <div className="flex justify-between text-green-500">
                                    <span>Desconto</span>
                                    <span>-{formatCurrency(discount)}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-xl font-bold pt-2 border-t border-[#262626] mt-2">
                                <span className="text-white">Total</span>
                                <span className="text-red-500">
                                    {formatCurrency(Math.max(0, (selectedTab?.subtotal || 0) - discount))}
                                </span>
                            </div>
                        </div>
                        
                        {/* Discount */}
                        <div className="space-y-2">
                            <Label className="text-gray-400">Desconto (R$)</Label>
                            <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={discount}
                                onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                                className="bg-[#121212] border-[#262626] text-white"
                            />
                        </div>
                        
                        {/* Payment Toggle */}
                        <div className="flex items-center justify-between p-3 bg-[#121212] rounded-lg">
                            <div>
                                <p className="text-white font-medium">Pagamento Dividido</p>
                                <p className="text-xs text-gray-500">Dividir em múltiplas formas</p>
                            </div>
                            <Button
                                size="sm"
                                variant={splitPaymentMode ? "default" : "outline"}
                                className={splitPaymentMode ? "bg-red-500" : "border-[#262626]"}
                                onClick={() => {
                                    setSplitPaymentMode(!splitPaymentMode);
                                    setPayments([]);
                                }}
                            >
                                {splitPaymentMode ? 'Ativo' : 'Inativo'}
                            </Button>
                        </div>
                        
                        {/* Single Payment */}
                        {!splitPaymentMode && (
                            <div className="space-y-4">
                                <Label className="text-gray-400">Forma de Pagamento</Label>
                                <div className="grid grid-cols-4 gap-2">
                                    {[
                                        { value: 'dinheiro', label: 'Dinheiro', icon: Banknote },
                                        { value: 'credito', label: 'Crédito', icon: CreditCard },
                                        { value: 'debito', label: 'Débito', icon: CreditCard },
                                        { value: 'pix', label: 'PIX', icon: QrCode },
                                    ].map(method => (
                                        <Button
                                            key={method.value}
                                            size="sm"
                                            variant={paymentMethod === method.value ? "default" : "outline"}
                                            className={`flex-col h-auto py-2 ${paymentMethod === method.value ? "bg-red-500" : "border-[#262626]"}`}
                                            onClick={() => setPaymentMethod(method.value)}
                                        >
                                            <method.icon className="w-4 h-4 mb-1" />
                                            <span className="text-xs">{method.label}</span>
                                        </Button>
                                    ))}
                                </div>
                                
                                {/* Fiado option */}
                                <Button
                                    variant={paymentMethod === 'fiado' ? "default" : "outline"}
                                    className={`w-full ${paymentMethod === 'fiado' ? "bg-orange-500 hover:bg-orange-400" : "border-orange-500/30 text-orange-500 hover:bg-orange-500/10"}`}
                                    onClick={() => setPaymentMethod('fiado')}
                                >
                                    <Clock className="w-4 h-4 mr-2" />
                                    Fiado (Pagar Depois)
                                </Button>
                                
                                {/* Cash fields */}
                                {paymentMethod === 'dinheiro' && (
                                    <div className="grid grid-cols-2 gap-4 p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                                        <div className="space-y-2">
                                            <Label className="text-gray-400">Valor Recebido</Label>
                                            <Input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={amountPaid}
                                                onChange={(e) => setAmountPaid(e.target.value)}
                                                className="bg-[#121212] border-[#262626] text-white"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-gray-400">Troco</Label>
                                            <div className="h-10 px-3 bg-[#0a0a0a] border border-[#262626] rounded-md flex items-center">
                                                <span className={`font-bold ${changeGiven > 0 ? 'text-amber-500' : 'text-gray-500'}`}>
                                                    {formatCurrency(changeGiven)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Fiado date */}
                                {paymentMethod === 'fiado' && (
                                    <div className="space-y-2 p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
                                        <Label className="text-gray-400 flex items-center gap-2">
                                            <Calendar className="w-4 h-4" />
                                            Data de Pagamento
                                        </Label>
                                        <Input
                                            type="date"
                                            value={fiadoDueDate}
                                            onChange={(e) => setFiadoDueDate(e.target.value)}
                                            className="bg-[#121212] border-[#262626] text-white"
                                            min={new Date().toISOString().split('T')[0]}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {/* Split Payment */}
                        {splitPaymentMode && (
                            <div className="space-y-4">
                                {/* Added payments */}
                                {payments.length > 0 && (
                                    <div className="space-y-2">
                                        {payments.map((p, i) => (
                                            <div key={i} className="flex items-center justify-between p-2 bg-[#121212] rounded-lg">
                                                <span className="text-white capitalize">{p.method}</span>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-bold text-white">{formatCurrency(p.amount)}</span>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-6 w-6 p-0 text-red-500"
                                                        onClick={() => removePayment(i)}
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-400">Total pago</span>
                                            <span className="text-green-500 font-bold">
                                                {formatCurrency(payments.reduce((sum, p) => sum + p.amount, 0))}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Add payment */}
                                <div className="p-3 bg-[#121212] rounded-lg space-y-3">
                                    <div className="grid grid-cols-4 gap-2">
                                        {['dinheiro', 'credito', 'debito', 'pix'].map(m => (
                                            <Button
                                                key={m}
                                                size="sm"
                                                variant={currentPaymentMethod === m ? "default" : "outline"}
                                                className={`text-xs ${currentPaymentMethod === m ? "bg-red-500" : "border-[#262626]"}`}
                                                onClick={() => setCurrentPaymentMethod(m)}
                                            >
                                                {m.charAt(0).toUpperCase() + m.slice(1)}
                                            </Button>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <Input
                                            type="number"
                                            min="0.01"
                                            step="0.01"
                                            value={currentPaymentAmount}
                                            onChange={(e) => setCurrentPaymentAmount(e.target.value)}
                                            placeholder="Valor"
                                            className="bg-[#1a1a1a] border-[#262626] text-white"
                                        />
                                        <Button onClick={addPayment} className="bg-red-500 text-white">
                                            <Plus className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCloseTabDialogOpen(false)} className="border-[#262626] text-gray-400">
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleCloseTab}
                            disabled={processing}
                            className="bg-red-500 text-white hover:bg-red-400 font-bold"
                        >
                            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                                paymentMethod === 'fiado' ? 'Registrar Fiado' : 'Confirmar Pagamento'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Fiados List Dialog */}
            <Dialog open={fiadosDialogOpen} onOpenChange={setFiadosDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Clock className="w-5 h-5 text-orange-500" />
                            Fiados Pendentes
                        </DialogTitle>
                    </DialogHeader>
                    
                    <ScrollArea className="max-h-[60vh]">
                        <div className="space-y-3 py-4">
                            {fiados.map(fiado => (
                                <div
                                    key={fiado.id}
                                    className="p-4 bg-[#121212] rounded-lg border border-orange-500/30"
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="font-bold text-white">{fiado.customer_name}</h3>
                                        <span className="text-xl font-bold text-orange-500">
                                            {formatCurrency(fiado.total)}
                                        </span>
                                    </div>
                                    <div className="text-sm text-gray-400 mb-3">
                                        <p>Vence: {new Date(fiado.fiado_due_date).toLocaleDateString('pt-BR')}</p>
                                        <p>{fiado.items?.length || 0} itens</p>
                                    </div>
                                    
                                    {/* Detalhamento dos items */}
                                    <div className="bg-[#0a0a0a] rounded p-3 mb-3 max-h-40 overflow-y-auto">
                                        <p className="text-xs text-gray-500 mb-2">Items:</p>
                                        {fiado.items?.map((item, idx) => (
                                            <div key={idx} className="flex justify-between text-xs py-1 border-b border-gray-800 last:border-0">
                                                <span className="text-gray-300">{item.quantity}x {item.product_name}</span>
                                                <span className="text-white">{formatCurrency(item.total_price)}</span>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                                setSelectedTab(fiado);
                                                setTabDetailOpen(true);
                                                setFiadosDialogOpen(false);
                                            }}
                                            className="border-blue-500 text-blue-500 hover:bg-blue-500/20 flex-1"
                                        >
                                            <Eye className="w-4 h-4 mr-1" />
                                            Editar Items
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() => {
                                                setSelectedFiado(fiado);
                                                resetPaymentForm();
                                                setPayFiadoDialogOpen(true);
                                            }}
                                            className="bg-green-500 text-white hover:bg-green-400 flex-1"
                                        >
                                            <CreditCard className="w-4 h-4 mr-1" />
                                            Receber
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleCancelFiado(fiado)}
                                            className="border-red-500 text-red-500 hover:bg-red-500/20"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </ScrollArea>
                </DialogContent>
            </Dialog>

            {/* Pay Fiado Dialog */}
            <Dialog open={payFiadoDialogOpen} onOpenChange={setPayFiadoDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white">
                    <DialogHeader>
                        <DialogTitle>Receber Fiado - {selectedFiado?.customer_name}</DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="p-4 bg-[#121212] rounded-lg text-center">
                            <p className="text-gray-400 mb-1">Total a Receber</p>
                            <p className="text-3xl font-bold text-orange-500">{formatCurrency(selectedFiado?.total)}</p>
                        </div>
                        
                        <Label className="text-gray-400">Forma de Pagamento</Label>
                        <div className="grid grid-cols-4 gap-2">
                            {[
                                { value: 'dinheiro', label: 'Dinheiro', icon: Banknote },
                                { value: 'credito', label: 'Crédito', icon: CreditCard },
                                { value: 'debito', label: 'Débito', icon: CreditCard },
                                { value: 'pix', label: 'PIX', icon: QrCode },
                            ].map(method => (
                                <Button
                                    key={method.value}
                                    size="sm"
                                    variant={paymentMethod === method.value ? "default" : "outline"}
                                    className={`flex-col h-auto py-2 ${paymentMethod === method.value ? "bg-red-500" : "border-[#262626]"}`}
                                    onClick={() => setPaymentMethod(method.value)}
                                >
                                    <method.icon className="w-4 h-4 mb-1" />
                                    <span className="text-xs">{method.label}</span>
                                </Button>
                            ))}
                        </div>
                        
                        {paymentMethod === 'dinheiro' && (
                            <div className="grid grid-cols-2 gap-4 p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                                <div className="space-y-2">
                                    <Label className="text-gray-400">Valor Recebido</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={amountPaid}
                                        onChange={(e) => {
                                            setAmountPaid(e.target.value);
                                            const paid = parseFloat(e.target.value) || 0;
                                            setChangeGiven(Math.max(0, paid - (selectedFiado?.total || 0)));
                                        }}
                                        className="bg-[#121212] border-[#262626] text-white"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-gray-400">Troco</Label>
                                    <div className="h-10 px-3 bg-[#0a0a0a] border border-[#262626] rounded-md flex items-center">
                                        <span className={`font-bold ${changeGiven > 0 ? 'text-amber-500' : 'text-gray-500'}`}>
                                            {formatCurrency(changeGiven)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPayFiadoDialogOpen(false)} className="border-[#262626] text-gray-400">
                            Cancelar
                        </Button>
                        <Button
                            onClick={handlePayFiado}
                            disabled={processing}
                            className="bg-green-500 text-white hover:bg-green-400 font-bold"
                        >
                            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar Recebimento'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white">
                    <DialogHeader>
                        <DialogTitle>Excluir Comanda?</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-gray-400">
                            Tem certeza que deseja excluir a comanda de <strong className="text-white">{selectedTab?.customer_name}</strong>?
                        </p>
                        <p className="text-gray-400 mt-2">
                            Esta ação não pode ser desfeita e o estoque dos produtos será devolvido.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => setDeleteConfirmOpen(false)}
                            disabled={processing}
                            className="border-[#262626] text-gray-400"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleDeleteTab}
                            disabled={processing}
                            className="bg-red-500 text-white hover:bg-red-400"
                        >
                            {processing ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    Excluindo...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Excluir
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ComandasPage;
